import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Trash2, Plus, UserPlus, Edit, Search, Check, ChevronsUpDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useState as useDebounceState, useEffect as useDebounceEffect } from 'react';
import { cn } from '@/lib/utils';

interface MentorAssignment {
  id: string;
  mentor_id: string;
  mentee_id: string;
  academic_year: string;
  assignment_start_date: string;
  assignment_end_date: string | null;
  status: string;
  program_type: string;
  notes: string | null;
  mentor_profile?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  mentee_profile?: {
    first_name: string;
    last_name: string;
    email: string;
    teaching_experience_years?: number;
  };
}

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  is_mentor: boolean;
  is_new_teacher_program: boolean;
  teaching_experience_years?: number;
}

interface MentorAssignmentManagerProps {
  onAssignmentChange?: () => void;
}

export function MentorAssignmentManager({ onAssignmentChange }: MentorAssignmentManagerProps) {
  const [assignments, setAssignments] = useState<MentorAssignment[]>([]);
  const [mentors, setMentors] = useState<Profile[]>([]);
  const [newTeachers, setNewTeachers] = useState<Profile[]>([]);
  const [filteredMentors, setFilteredMentors] = useState<Profile[]>([]);
  const [filteredNewTeachers, setFilteredNewTeachers] = useState<Profile[]>([]);
  const [mentorSearch, setMentorSearch] = useState('');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [mentorOpen, setMentorOpen] = useState(false);
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    mentor_id: '',
    mentee_id: '',
    academic_year: new Date().getFullYear().toString(),
    assignment_start_date: new Date().toISOString().split('T')[0],
    assignment_end_date: '',
    program_type: 'new_teacher',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Filter mentors based on search
  useEffect(() => {
    if (!mentorSearch.trim()) {
      setFilteredMentors(mentors.slice(0, 50)); // Show first 50 when no search
      return;
    }

    const search = mentorSearch.toLowerCase();
    const filtered = mentors.filter(mentor => 
      `${mentor.first_name} ${mentor.last_name}`.toLowerCase().includes(search) ||
      mentor.email.toLowerCase().includes(search)
    );
    setFilteredMentors(filtered.slice(0, 50)); // Limit to 50 results
  }, [mentorSearch, mentors]);

  // Filter new teachers based on search  
  useEffect(() => {
    if (!teacherSearch.trim()) {
      setFilteredNewTeachers(newTeachers.slice(0, 50)); // Show first 50 when no search
      return;
    }

    const search = teacherSearch.toLowerCase();
    const filtered = newTeachers.filter(teacher => 
      `${teacher.first_name} ${teacher.last_name}`.toLowerCase().includes(search) ||
      teacher.email.toLowerCase().includes(search)
    );
    setFilteredNewTeachers(filtered.slice(0, 50)); // Limit to 50 results  
  }, [teacherSearch, newTeachers]);

  const loadData = async () => {
    try {
      // Load mentor assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('mentor_assignments')
        .select('*')
        .order('created_at', { ascending: false });

      if (assignmentsError) throw assignmentsError;

      // Enrich with profile data
      const enrichedAssignments = [];
      for (const assignment of assignmentsData || []) {
        // Get mentor profile
        const { data: mentorProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name, email')
          .eq('id', assignment.mentor_id)
          .single();

        // Get mentee profile
        const { data: menteeProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name, email, teaching_experience_years')
          .eq('id', assignment.mentee_id)
          .single();

        enrichedAssignments.push({
          ...assignment,
          mentor_profile: mentorProfile,
          mentee_profile: menteeProfile
        });
      }

      // Load available mentors - start with profiles, then fallback to demo_staff
      let mentorsData = [];
      const { data: profileMentors, error: mentorsError } = await supabase
        .from('profiles')
        .select('*')
        .eq('is_mentor', true)
        .order('last_name');

      if (mentorsError) console.error('Error loading profile mentors:', mentorsError);
      
      // If no mentors in profiles, use demo_staff with senior positions as mentors
      if (!profileMentors || profileMentors.length === 0) {
        const { data: demoMentors, error: demoMentorsError } = await supabase
          .from('demo_staff')
          .select('*, id:employee_id')
          .in('position_title', ['Lead Teacher', 'Department Head', 'Master Teacher', 'Curriculum Specialist', 'Assistant Principal', 'Athletic Director', 'Arts Coordinator'])
          .order('last_name');

        if (demoMentorsError) throw demoMentorsError;
        
        // Map demo_staff to profile format
        mentorsData = (demoMentors || []).map(mentor => ({
          ...mentor,
          id: mentor.employee_id,
          is_mentor: true,
          teaching_experience_years: Math.floor(Math.random() * 15 + 5) // 5-20 years for senior staff
        }));
      } else {
        mentorsData = profileMentors;
      }

      // Load new teachers - start with profiles, then fallback to demo_staff
      let newTeachersData = [];
      const { data: profileNewTeachers, error: newTeachersError } = await supabase
        .from('profiles')
        .select('*')
        .eq('is_new_teacher_program', true)
        .order('last_name');

      if (newTeachersError) console.error('Error loading profile new teachers:', newTeachersError);

      // If no new teachers in profiles, use demo_staff (regular teachers and recent hires)
      if (!profileNewTeachers || profileNewTeachers.length === 0) {
        const { data: demoNewTeachers, error: demoNewTeachersError } = await supabase
          .from('demo_staff')
          .select('*, id:employee_id')
          .eq('job_type', 'Teacher')
          .not('position_title', 'in', '(Lead Teacher,Department Head,Master Teacher,Curriculum Specialist)')
          .order('last_name');

        if (demoNewTeachersError) throw demoNewTeachersError;
        
        // Map demo_staff to profile format and mark as new teachers
        newTeachersData = (demoNewTeachers || []).map(teacher => ({
          ...teacher,
          id: teacher.employee_id,
          is_new_teacher_program: true,
          teaching_experience_years: Math.floor(Math.random() * 3) // 0-2 years for new teachers
        }));
      } else {
        newTeachersData = profileNewTeachers;
      }

      setAssignments(enrichedAssignments);
      setMentors(mentorsData || []);
      setNewTeachers(newTeachersData || []);
      setFilteredMentors((mentorsData || []).slice(0, 50));
      setFilteredNewTeachers((newTeachersData || []).slice(0, 50));
    } catch (error) {
      console.error('Error loading mentor assignment data:', error);
      toast.error('Failed to load mentor assignment data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const assignmentData = {
        ...formData,
        assignment_end_date: formData.assignment_end_date || null
      };

      const { error } = await supabase
        .from('mentor_assignments')
        .insert([assignmentData]);

      if (error) throw error;

      toast.success('Mentor assignment created successfully');
      setShowCreateDialog(false);
      setFormData({
        mentor_id: '',
        mentee_id: '',
        academic_year: new Date().getFullYear().toString(),
        assignment_start_date: new Date().toISOString().split('T')[0],
        assignment_end_date: '',
        program_type: 'new_teacher',
        notes: ''
      });
      loadData();
      onAssignmentChange?.();
    } catch (error) {
      console.error('Error creating mentor assignment:', error);
      toast.error('Failed to create mentor assignment');
    }
  };

  const handleDeleteAssignment = async (id: string) => {
    if (!confirm('Are you sure you want to delete this mentor assignment?')) return;

    try {
      const { error } = await supabase
        .from('mentor_assignments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Mentor assignment deleted successfully');
      loadData();
      onAssignmentChange?.();
    } catch (error) {
      console.error('Error deleting mentor assignment:', error);
      toast.error('Failed to delete mentor assignment');
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('mentor_assignments')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      toast.success('Assignment status updated');
      loadData();
      onAssignmentChange?.();
    } catch (error) {
      console.error('Error updating assignment status:', error);
      toast.error('Failed to update assignment status');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading mentor assignments...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Mentor Assignments</CardTitle>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                New Assignment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Mentor Assignment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAssignment} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mentor_id">Mentor</Label>
                    <Popover open={mentorOpen} onOpenChange={setMentorOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={mentorOpen}
                          className="w-full justify-between"
                        >
                          {formData.mentor_id
                            ? mentors.find(mentor => mentor.id === formData.mentor_id)?.first_name + ' ' +
                              mentors.find(mentor => mentor.id === formData.mentor_id)?.last_name
                            : "Select mentor..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput 
                            placeholder="Search mentors..." 
                            value={mentorSearch}
                            onValueChange={setMentorSearch}
                          />
                          <CommandList>
                            <CommandEmpty>No mentors found.</CommandEmpty>
                            <CommandGroup>
                              {filteredMentors.map((mentor) => (
                                <CommandItem
                                  key={mentor.id}
                                  value={`${mentor.first_name} ${mentor.last_name} ${mentor.email}`}
                                  onSelect={() => {
                                    setFormData(prev => ({ ...prev, mentor_id: mentor.id }));
                                    setMentorOpen(false);
                                    setMentorSearch('');
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.mentor_id === mentor.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {mentor.first_name} {mentor.last_name}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                      {mentor.email}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div>
                    <Label htmlFor="mentee_id">New Teacher</Label>
                    <Popover open={teacherOpen} onOpenChange={setTeacherOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={teacherOpen}
                          className="w-full justify-between"
                        >
                          {formData.mentee_id
                            ? newTeachers.find(teacher => teacher.id === formData.mentee_id)?.first_name + ' ' +
                              newTeachers.find(teacher => teacher.id === formData.mentee_id)?.last_name
                            : "Select new teacher..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput 
                            placeholder="Search teachers..." 
                            value={teacherSearch}
                            onValueChange={setTeacherSearch}
                          />
                          <CommandList>
                            <CommandEmpty>No teachers found.</CommandEmpty>
                            <CommandGroup>
                              {filteredNewTeachers.map((teacher) => (
                                <CommandItem
                                  key={teacher.id}
                                  value={`${teacher.first_name} ${teacher.last_name} ${teacher.email}`}
                                  onSelect={() => {
                                    setFormData(prev => ({ ...prev, mentee_id: teacher.id }));
                                    setTeacherOpen(false);
                                    setTeacherSearch('');
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.mentee_id === teacher.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {teacher.first_name} {teacher.last_name}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                      {teacher.email}
                                      {teacher.teaching_experience_years !== undefined && (
                                        <span className="ml-2">
                                          ({teacher.teaching_experience_years} years)
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="academic_year">Academic Year</Label>
                    <Input
                      id="academic_year"
                      value={formData.academic_year}
                      onChange={(e) => setFormData(prev => ({ ...prev, academic_year: e.target.value }))}
                      placeholder="2024-2025"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="program_type">Program Type</Label>
                    <Select value={formData.program_type} onValueChange={(value) => 
                      setFormData(prev => ({ ...prev, program_type: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new_teacher">New Teacher</SelectItem>
                        <SelectItem value="coaching">Coaching</SelectItem>
                        <SelectItem value="peer_mentoring">Peer Mentoring</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="assignment_start_date">Start Date</Label>
                    <Input
                      id="assignment_start_date"
                      type="date"
                      value={formData.assignment_start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignment_start_date: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="assignment_end_date">End Date (Optional)</Label>
                    <Input
                      id="assignment_end_date"
                      type="date"
                      value={formData.assignment_end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignment_end_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about this assignment"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Create Assignment</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mentor</TableHead>
                <TableHead>New Teacher</TableHead>
                <TableHead>Academic Year</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {assignment.mentor_profile?.first_name} {assignment.mentor_profile?.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {assignment.mentor_profile?.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {assignment.mentee_profile?.first_name} {assignment.mentee_profile?.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {assignment.mentee_profile?.email}
                      </div>
                      {assignment.mentee_profile?.teaching_experience_years !== undefined && (
                        <Badge variant="outline" className="text-xs mt-1">
                          Year {assignment.mentee_profile.teaching_experience_years + 1}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{assignment.academic_year}</TableCell>
                  <TableCell>
                    {new Date(assignment.assignment_start_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Select 
                      value={assignment.status} 
                      onValueChange={(value) => handleStatusChange(assignment.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAssignment(assignment.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {assignments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No mentor assignments found. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}